import { t } from '@hefe/i18n';
import { RequestBasketballPostEvent } from '@hefe/models';
import { EventsApi, ModalsSlice } from '@hefe/store';
import { formatDatabaseDate } from '@hefe/utils';
import { Skeleton } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';

interface AddEditEventFormProps {
  eventId?: number;
  teamId?: number;
}

export default function AddEditEventForm(props: AddEditEventFormProps) {
  const { eventId, teamId } = props;

  const dispatch = useAppDispatch();

  const [refresh, doRefresh] = useState(false);

  useEffect(() => {
    doRefresh((prev) => !prev);
  }, [dispatch, eventId, teamId]);

  const { data: requestData, isFetching: isFetchingRequestData } =
    EventsApi.useFetchBasketballEventRequestDataQuery(eventId ?? 0, {
      skip: !eventId,
    });

  const { data: possibleSelectOptions, isFetching: isFetchingSelectOptions } =
    EventsApi.useFetchBasketballEventSelectOptionsQuery({
      eventId: eventId,
      teamId: teamId,
    });

  const [saveEvent, { isLoading: isSavingEvent }] =
    EventsApi.useSaveBasketballEventMutation();

  const initSelectOptions = useMemo(() => {
    // flatten the select options by options
    const options = possibleSelectOptions
      ?.map((section) => section.options)
      .flat();

    if (options) {
      const requestDataSelectOptions = requestData?.select_options ?? [];

      if (teamId) {
        // check if t_{teamId} is in the select options
        if (!requestDataSelectOptions.includes('t_' + teamId)) {
          requestDataSelectOptions.push('t_' + teamId);
        }
      }

      if (requestDataSelectOptions.length > 0) {
        const allOptions = options?.filter((option) =>
          requestDataSelectOptions?.includes(option.value as string)
        );
        return allOptions.map((opt) => opt.value);
      }
    }

    return [];
  }, [possibleSelectOptions, requestData?.select_options, teamId]);

  const onSubmit = (values: any, resetForm: any) => {
    if (teamId) {
      saveTeamEvent(values, resetForm);
    } else {
      saveClubEvent(values, resetForm);
    }
  };

  const saveClubEvent = (values: any, resetForm: any) => {
    const close = ModalsSlice.modalsActions.closeModal;
    const request: RequestBasketballPostEvent = {
      ...values,
      ...(eventId && { id: eventId }),
      date: formatDatabaseDate(values.date, true),
      end_date: formatDatabaseDate(values.end_date, true),
      select_options: values?.select_options,
      createable_id: 0,
      createable_type: 'App\\Models\\Base\\Club',
    };

    saveEvent(request)
      .unwrap()
      .then(() => {
        resetForm();
        dispatch(close());
      });
  };

  const saveTeamEvent = (values: any, resetForm: any) => {
    const close = ModalsSlice.modalsActions.closeModal;
    const request: RequestBasketballPostEvent = {
      ...values,
      ...(eventId && { id: eventId }),
      date: formatDatabaseDate(values.date, true),
      end_date: formatDatabaseDate(values.end_date, true),
      select_options: values.select_options,
      createable_id: teamId,
      createable_type: 'App\\Models\\Basketball\\BasketballTeam',
    };

    saveEvent(request)
      .unwrap()
      .then(() => {
        resetForm();
        dispatch(close());
      });
  };

  return (
    <Skeleton loading={isFetchingRequestData || isFetchingSelectOptions}>
      <CustomForm
        key={`add-edit-event-form-${eventId ?? 'new'}`}
        formId={`add-edit-event-form-${eventId ?? 'new'}`}
        refresh={refresh}
        fields={[
          {
            name: 'date',
            label: t('date'),
            type: 'datetime',
            initalValue: eventId ? requestData?.date : undefined,
            rules: [
              {
                required: true,
                message: t('form_errors.date'),
              },
            ],
          },
          {
            name: 'end_date',
            label: t('end'),
            type: 'datetime',
            initalValue: eventId ? requestData?.end_date : undefined,
            rules: [
              {
                required: true,
                message: t('form_errors.date'),
              },
            ],
          },
          {
            name: 'name',
            label: t('name'),
            type: 'text',
            initalValue: eventId ? requestData?.name : undefined,
            rules: [
              {
                required: true,
                message: t('form_errors.name'),
              },
            ],
          },
          {
            name: 'description',
            label: t('description'),
            type: 'text',
            initalValue: eventId ? requestData?.description : undefined,
            rules: [
              {
                required: true,
                message: t('form_errors.description'),
              },
            ],
          },
          {
            name: 'location',
            label: t('location'),
            type: 'text',
            initalValue: eventId ? requestData?.location : undefined,
            rules: [
              {
                required: true,
                message: t('form_errors.location'),
              },
            ],
          },
          {
            name: 'canceled',
            label: t('canceled'),
            type: 'checkbox',
            initalValue: eventId ? requestData?.canceled : undefined,
            rules: [],
          },
          {
            name: 'select_options',
            label: t('participants.plural'),
            type: 'select_groups',
            selectOptions: possibleSelectOptions ?? [],
            multiSelect: true,
            initalValue: initSelectOptions,
            disableSelectClear: true,
            rules: [],
          },
        ]}
        onSubmit={onSubmit}
        loading={isSavingEvent}
      />
    </Skeleton>
  );
}
